import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cancelOnboarding, changeTodoStatusV1 } from 'src/api/user';
import AppButton from 'src/components/base/Button/Button';
import MiniCheckBox from 'src/components/modules/MiniCheckBox/MiniCheckBox';
import { clearModalContent } from 'src/redux/modules/modalEvent';
import { updateIfollowedUpTodo } from 'src/redux/services/loggingServices';
import { Logger } from 'src/services';
import {
  FollowUpCheckboxCase,
  FollowUpTodo,
  FollowUpTodoArr,
} from '../../../constants/FollowUpTodo';
import { FollowUpTodoType } from './FollowUpTodoType';
import {
  CreditCardCollectionText,
  CreditCardTodoHeader,
  HighAcuityTodoHeader,
  IdentificationCardCollectionText,
  IdentificationCardTodoHeader,
  PolicyHolderCollectionText,
  PolicyHolderTodoHeader,
} from './FollowUpTodoComponents';
import { InfoTooltip } from '../../InfoTooltip';
import { Typography } from "src/latest/src/components";

interface FollowUpTodoModalProps {
  todoId: string;
  todo?: FollowUpTodoType;
  getUpdatedAuditLogs: () => void;
}

const FollowUpTodoModal: React.FC<FollowUpTodoModalProps> = ({
  todoId,
  todo,
  getUpdatedAuditLogs,
}) => {
  const dispatch = useDispatch();
  const [followUpArr, setFollowUpArr] = useState<string[]>([]);
  const [phoneActive, setphoneActive] = useState<boolean>(false);
  const [textActive, settextActive] = useState<boolean>(false);
  const [emailActive, setemailActive] = useState<boolean>(false);
  const [voiceActive, setvoiceActive] = useState<boolean>(false);
  const [note, setnote] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isInfoCollected, setIsInfoCollected] = useState<string>('');
  const [disableInfo, setDisableInfo] = useState<boolean>(false);
  const [ableToScreen, setAbleToScreen] = useState<string>('');
  const [clientAppropriate, setClientAppropriate] = useState<string>('');
  const EHR_NAME = useSelector((state: any) => state?.configuration?.ehrName);

  const followUpCheckBox = (source: string) => {
    switch (source) {
      case FollowUpCheckboxCase?.PHONE_CALL:
        return setphoneActive(!phoneActive);
      case FollowUpCheckboxCase?.TEXT:
        return settextActive(!textActive);
      case FollowUpCheckboxCase?.EMAIL:
        return setemailActive(!emailActive);
      case FollowUpCheckboxCase?.VOICE:
        return setvoiceActive(!voiceActive);
      case FollowUpCheckboxCase?.INFO_COLLECTED:
        return setIsInfoCollected(FollowUpCheckboxCase?.INFO_COLLECTED);
      case FollowUpCheckboxCase?.INFO_NOT_COLLECTED:
        return setIsInfoCollected(FollowUpCheckboxCase?.INFO_NOT_COLLECTED);
      case FollowUpCheckboxCase?.ABLE_TO_SCREEN:
        return setAbleToScreen(FollowUpCheckboxCase?.ABLE_TO_SCREEN);
      case FollowUpCheckboxCase?.NOT_ABLE_TO_SCREEN:
        return setAbleToScreen(FollowUpCheckboxCase?.NOT_ABLE_TO_SCREEN);
      case FollowUpCheckboxCase?.CLIENT_APPROPRIATE:
        return setClientAppropriate(FollowUpCheckboxCase?.CLIENT_APPROPRIATE);
      case FollowUpCheckboxCase?.CLINET_NOT_APPROPRIATE:
        return setClientAppropriate(
          FollowUpCheckboxCase?.CLINET_NOT_APPROPRIATE,
        );
      default:
        return '';
    }
  };

  useEffect(() => {
    if (!phoneActive && followUpArr?.includes(FollowUpTodoArr?.PHONE)) {
      setFollowUpArr(
        followUpArr?.filter(followup => followup !== FollowUpTodoArr?.PHONE),
      );
    }

    if (phoneActive && !followUpArr?.includes(FollowUpTodoArr?.PHONE)) {
      setFollowUpArr([...followUpArr, FollowUpTodoArr?.PHONE]);
    }
  }, [phoneActive]);

  useEffect(() => {
    if (!textActive && followUpArr?.includes(FollowUpTodoArr?.TEXT)) {
      setFollowUpArr(
        followUpArr?.filter(followup => followup !== FollowUpTodoArr?.TEXT),
      );
    }

    if (textActive && !followUpArr?.includes(FollowUpTodoArr?.TEXT)) {
      setFollowUpArr([...followUpArr, FollowUpTodoArr?.TEXT]);
    }
  }, [textActive]);

  useEffect(() => {
    if (!emailActive && followUpArr?.includes(FollowUpTodoArr?.EMAIL)) {
      setFollowUpArr(
        followUpArr?.filter(followup => followup !== FollowUpTodoArr?.EMAIL),
      );
    }

    if (emailActive && !followUpArr?.includes(FollowUpTodoArr?.EMAIL)) {
      setFollowUpArr([...followUpArr, FollowUpTodoArr?.EMAIL]);
    }
  }, [emailActive]);

  useEffect(() => {
    if (!voiceActive && followUpArr?.includes(FollowUpTodoArr?.VOICE_EMAIL)) {
      setFollowUpArr(
        followUpArr?.filter(
          followup => followup !== FollowUpTodoArr?.VOICE_EMAIL,
        ),
      );
    }

    if (voiceActive && !followUpArr?.includes(FollowUpTodoArr?.VOICE_EMAIL)) {
      setFollowUpArr([...followUpArr, FollowUpTodoArr?.VOICE_EMAIL]);
    }
  }, [voiceActive]);

  useEffect(() => {
    if (
      isInfoCollected === FollowUpCheckboxCase?.INFO_COLLECTED ||
      isInfoCollected === FollowUpCheckboxCase?.INFO_NOT_COLLECTED
    ) {
      setDisableInfo(true);
    }

    if (ableToScreen === FollowUpCheckboxCase?.NOT_ABLE_TO_SCREEN) {
      setClientAppropriate('');
      setDisableInfo(true);
    }

    if (ableToScreen === FollowUpCheckboxCase?.ABLE_TO_SCREEN) {
      setDisableInfo(false);

      if (
        clientAppropriate === FollowUpCheckboxCase?.CLIENT_APPROPRIATE ||
        clientAppropriate === FollowUpCheckboxCase?.CLINET_NOT_APPROPRIATE
      ) {
        setDisableInfo(true);
      }
    }
  }, [isInfoCollected, ableToScreen, clientAppropriate]);

  const editNoteContent = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setnote(e?.target?.value);
  };

  // Handle form submission
  const handleSubmitFollowUp = async () => {
    const respObj: { note: string; follow_up_by?: string[] } = { note };

    // Include follow-up methods only if selected
    if (followUpArr?.length > 0) {
      respObj.follow_up_by = followUpArr; // ["PHONE", "EMAIL"]
    }

    try {
      setLoading(true);

      if (todo?.type === FollowUpTodo?.HIGH_ACUITY_TODO) {
        if (
          ableToScreen === FollowUpCheckboxCase?.ABLE_TO_SCREEN &&
          clientAppropriate === FollowUpCheckboxCase?.CLIENT_APPROPRIATE
        ) {
          await updateIfollowedUpTodo(todoId, respObj);
          dispatch(clearModalContent());
          getUpdatedAuditLogs();
        } else if (
          clientAppropriate === FollowUpCheckboxCase?.CLINET_NOT_APPROPRIATE
        ) {
          await cancelOnboarding(todo?.user_id, note);
          dispatch(clearModalContent());
          getUpdatedAuditLogs();
        } else {
          await changeTodoStatusV1(todoId, false, note);
          dispatch(clearModalContent());
          getUpdatedAuditLogs();
        }
      } else {
        if (
          isInfoCollected === FollowUpCheckboxCase?.INFO_COLLECTED ||
          !(
            todo?.type === FollowUpTodo?.CREDIT_CARD_TODO ||
            todo?.type === FollowUpTodo?.POLICY_HOLDER_TODO ||
            todo?.type === FollowUpTodo?.IDENTIFICATION_CARD_TODO
          )
        ) {
          await updateIfollowedUpTodo(todoId, respObj);
          dispatch(clearModalContent());
          getUpdatedAuditLogs();
        } else {
          await changeTodoStatusV1(todoId, false, note);
          dispatch(clearModalContent());
          getUpdatedAuditLogs();
        }
      }
    } catch (e) {
      Logger?.error(e);
    } finally {
      setLoading(false);
    }
  };

  const renderTodoHeader = () => {
    switch (todo?.type) {
      case FollowUpTodo?.POLICY_HOLDER_TODO:
        return <PolicyHolderTodoHeader userFullName={todo?.user_full_name} />;

      case FollowUpTodo?.IDENTIFICATION_CARD_TODO:
        return (
          <IdentificationCardTodoHeader
            userFullName={todo?.user_full_name}
            ehrName={EHR_NAME}
          />
        );

      case FollowUpTodo?.CREDIT_CARD_TODO:
        return <CreditCardTodoHeader userFullName={todo?.user_full_name} />;

      case FollowUpTodo?.HIGH_ACUITY_TODO:
        return (
          <HighAcuityTodoHeader
            userFullName={todo?.user_full_name}
            metadata={todo?.metadata}
          />
        );

      default:
        return <></>;
    }
  };

  const renderCollectionText = () => {
    switch (todo?.type) {
      case FollowUpTodo?.CREDIT_CARD_TODO:
        return <CreditCardCollectionText />;

      case FollowUpTodo?.POLICY_HOLDER_TODO:
        return <PolicyHolderCollectionText />;

      case FollowUpTodo?.IDENTIFICATION_CARD_TODO:
        return <IdentificationCardCollectionText />;

      default:
        return <></>;
    }
  };

  return (
    <div className="add-note-text-area d-flex flex-column justify-content-center follow-up-modal">
      <div className="mb-4 d-flex flex-column">
        {renderTodoHeader()}
        <div className="mb-4">
          <Typography>How did you follow up?</Typography>
        </div>
        <div className="d-flex flex-row m-auto checkbox-container justify-content-between">
          <div className="d-flex flex-column justify-content-start">
            <div className="mb-1 ">
              <MiniCheckBox
                text="Phone Call"
                isCheckFirst={true}
                isActive={phoneActive}
                onClick={() => followUpCheckBox('phone-call')}
                value={phoneActive}
              />
            </div>
            <div className="">
              <MiniCheckBox
                text="Text"
                isCheckFirst={true}
                isActive={textActive}
                onClick={() => followUpCheckBox('text')}
                value={textActive}
              />
            </div>
          </div>
          <div className="d-flex  flex-column justify-content-start me-2">
            <div className="mb-1">
              <MiniCheckBox
                text="Voicemail"
                isCheckFirst={true}
                isActive={voiceActive}
                onClick={() => followUpCheckBox('voice')}
                value={voiceActive}
              />
            </div>
            <div className="">
              <MiniCheckBox
                text="Email"
                isCheckFirst={true}
                isActive={emailActive}
                onClick={() => followUpCheckBox('email')}
                value={emailActive}
              />
            </div>
          </div>
        </div>
      </div>
      {(todo?.type === FollowUpTodo?.CREDIT_CARD_TODO ||
        todo?.type === FollowUpTodo?.POLICY_HOLDER_TODO ||
        todo?.type === FollowUpTodo?.IDENTIFICATION_CARD_TODO) && (
        <div>
          <div className="mb-4 ms-5 me-5">{renderCollectionText()}</div>
          <div className="d-flex flex-row m-auto checkbox-container justify-content-between">
            <div className="d-flex flex-column justify-content-start">
              <div className="mb-1 ">
                <MiniCheckBox
                  text="Yes"
                  isCheckFirst={true}
                  isActive={
                    isInfoCollected === FollowUpCheckboxCase?.INFO_COLLECTED
                  }
                  onClick={() =>
                    followUpCheckBox(FollowUpCheckboxCase?.INFO_COLLECTED)
                  }
                  value={phoneActive}
                />
              </div>
            </div>
            <div className="d-flex  flex-column justify-content-start me-5">
              <div className="mb-1">
                <MiniCheckBox
                  text="No"
                  isCheckFirst={true}
                  isActive={
                    isInfoCollected === FollowUpCheckboxCase?.INFO_NOT_COLLECTED
                  }
                  onClick={() =>
                    followUpCheckBox(FollowUpCheckboxCase?.INFO_NOT_COLLECTED)
                  }
                  value={isInfoCollected}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {todo?.type === FollowUpTodo?.HIGH_ACUITY_TODO && (
        <div>
          <div className="mb-4 ms-5 me-5">
            <Typography variant="size-16">
              Were you able to screen this patient for high acuity?
            </Typography>
          </div>
          <div className="d-flex flex-row m-auto checkbox-container justify-content-between">
            <div className="d-flex flex-column justify-content-start">
              <div className="mb-1 ">
                <MiniCheckBox
                  text="Yes"
                  isCheckFirst={true}
                  isActive={
                    ableToScreen === FollowUpCheckboxCase?.ABLE_TO_SCREEN
                  }
                  onClick={() =>
                    followUpCheckBox(FollowUpCheckboxCase?.ABLE_TO_SCREEN)
                  }
                  value={phoneActive}
                  isRadio={true}
                />
              </div>
            </div>
            <div className="d-flex  flex-column justify-content-start me-5">
              <div className="mb-1 d-flex">
                <MiniCheckBox
                  text="No"
                  isCheckFirst={true}
                  isActive={
                    ableToScreen === FollowUpCheckboxCase?.NOT_ABLE_TO_SCREEN
                  }
                  onClick={() =>
                    followUpCheckBox(FollowUpCheckboxCase?.NOT_ABLE_TO_SCREEN)
                  }
                  isRadio={true}
                  value={ableToScreen}
                />{' '}
                <InfoTooltip
                  className="position-absolute acuity-info1 mt-1"
                  description={
                    <>
                      Answering{' '}
                      <strong>
                        <i>No</i>
                      </strong>{' '}
                      will snooze this TODO and move it to Pending for the next
                      24 hours. If you get the information you need, you can
                      find and open this TODO from your Pending TODO list on
                      your Scheduler Dashboard.
                    </>
                  }
                />
              </div>
            </div>
          </div>
          {ableToScreen === FollowUpCheckboxCase?.ABLE_TO_SCREEN && (
            <div>
              <div className="mb-4 ms-5 me-5">
                <Typography variant="size-16">
                  Is this patient appropriate to receive services from your
                  clinic?
                </Typography>
              </div>
              <div className="d-flex flex-row m-auto checkbox-container justify-content-between">
                <div className="d-flex flex-column justify-content-start">
                  <div className="mb-1 ">
                    <MiniCheckBox
                      text="Yes"
                      isCheckFirst={true}
                      isActive={
                        clientAppropriate ===
                        FollowUpCheckboxCase?.CLIENT_APPROPRIATE
                      }
                      onClick={() =>
                        followUpCheckBox(
                          FollowUpCheckboxCase?.CLIENT_APPROPRIATE,
                        )
                      }
                      value={phoneActive}
                      isRadio={true}
                    />
                  </div>
                </div>
                <div className="d-flex  flex-column justify-content-start me-5">
                  <div className="mb-1 d-flex">
                    <MiniCheckBox
                      text="No"
                      isCheckFirst={true}
                      isActive={
                        clientAppropriate ===
                        FollowUpCheckboxCase?.CLINET_NOT_APPROPRIATE
                      }
                      onClick={() =>
                        followUpCheckBox(
                          FollowUpCheckboxCase?.CLINET_NOT_APPROPRIATE,
                        )
                      }
                      isRadio={true}
                      value={clientAppropriate}
                    />{' '}
                    <InfoTooltip
                      className="position-absolute acuity-info1 mt-1"
                      description={
                        <>
                          The patient's onboarding process will be{' '}
                          <strong>
                            <i>paused</i>
                          </strong>{' '}
                          and the intake appointment they requested won't be
                          confirmed.
                        </>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div>
        <div
          className={`d-flex justify-content-center mt-3 ${
            todo?.type === FollowUpTodo?.IDENTIFICATION_CARD_TODO ? 'mt-4' : ''
          }`}
        >
          <Typography className="me-2">Add Contact Note </Typography>{' '}
          <Typography color="color-28">(optional)</Typography>
          {todo?.type === FollowUpTodo?.IDENTIFICATION_CARD_TODO && (
            <InfoTooltip
              className="position-absolute acuity-info1"
              description={
                <>
                  Answering{' '}
                  <strong>
                    <i>No</i>
                  </strong>{' '}
                  will snooze this TODO and move it to Pending for the next 24
                  hours. If you get the information you need, you can find and
                  open this TODO from your Pending TODO list on your Scheduler
                  Dashboard.
                </>
              }
            />
          )}
        </div>
        <textarea
          rows={4}
          maxLength={500}
          placeholder="Add Note"
          value={note}
          onChange={editNoteContent}
        />
        <AppButton
          disabled={
            !disableInfo &&
            (todo?.type === FollowUpTodo?.CREDIT_CARD_TODO ||
              todo?.type === FollowUpTodo?.HIGH_ACUITY_TODO ||
              todo?.type === FollowUpTodo?.POLICY_HOLDER_TODO ||
              todo?.type === FollowUpTodo?.IDENTIFICATION_CARD_TODO)
          }
          label="Save"
          variant="primary"
          textWeight="bold"
          textSize="size-14"
          isProcessing={loading}
          className="m-3 modal-btn confirmation-modal__no-button"
          onClick={handleSubmitFollowUp}
        />
      </div>
    </div>
  );
};

export default FollowUpTodoModal;
